import {
  getAllPlacesWithReviews,
  getAllPlaceDetailsByPlaceId,
  getAllPlaceMenusByPlaceId,
  getAllPlacesWithReviewsByPlaceId,
  getPlacePostsByPlaceId,
  getPlaceTextReviewByPlaceId,
} from "@/api/places";
import { calculateDistanceBetweenCoords } from "@/utils/helpers";
import Iconify from "@/components/ui/Iconify";
import { enqueueSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import PDW_PlaceMediaPhotoGrid from "../../places-details-window/pdw-place-media-photo-grid";
import PDW_PlaceDescription from "../../places-details-window/pdw-place-description";
import PDW_PlaceTabsSection from "../../places-details-window/pdw-place-tab-section";
import { useTranslations } from "@/lib/static-intl";
import { useAuthContext } from "@/context/auth/useAuthContext";
import PlaceFavSliderModal from "../../rf-place-by-id-window/places-fav-slider-modal";
import { usePlaceViewNavigation } from "@/hooks/usePlaceViewNavigation";

const normalizePlaceName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const getPlaceCoords = (place) => {
  const latitude = Number(place?.latitude);
  const longitude = Number(place?.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { latitude, longitude };
};

const isLikelySamePlace = (a, b) => {
  const aName = normalizePlaceName(a?.name);
  const bName = normalizePlaceName(b?.name);
  if (!aName || aName !== bName) return false;

  const aCoords = getPlaceCoords(a);
  const bCoords = getPlaceCoords(b);
  if (!aCoords || !bCoords) return false;

  return calculateDistanceBetweenCoords(aCoords, bCoords) <= 80;
};

const getPlaceCompletenessScore = (place) => {
  const mediaCount = Array.isArray(place?.placeMedia) ? place.placeMedia.length : 0;
  const reviewCount = Number(
    place?.totalDbReviews || place?.numberOfVisitorReviews || place?.totalReviews || 0
  );
  const hasPhone = place?.phoneNumber || place?.phone ? 1 : 0;
  const hasHours = place?.businessHours || place?.kakaoBusinessHours ? 1 : 0;
  return mediaCount * 10 + reviewCount + hasPhone + hasHours;
};

const resolveRicherCanonicalPlace = async (place, userId) => {
  if (!place?.name || place?.source !== "api") return place;

  const response = await getAllPlacesWithReviews({
    searchQuery: place.name,
    userId,
    page: 1,
    limit: 10,
  });
  const candidates = Array.isArray(response?.places)
    ? response.places
    : Array.isArray(response)
      ? response
      : [];
  const richerPlace = candidates
    .filter((candidate) => candidate?.source !== "api")
    .filter((candidate) => isLikelySamePlace(candidate, place))
    .sort(
      (a, b) => getPlaceCompletenessScore(b) - getPlaceCompletenessScore(a)
    )[0];

  return richerPlace || place;
};

const PlaceDetailsModalForMobile = ({
  placeId,
  handleReset,
  handleMapRoutesArrive,
  handleMapRoutesDepart,
  handlePostCardClick,
  userFollowing,
  setUserFollowing,
  userLocation,
}) => {
  const { user } = useAuthContext();
  const t = useTranslations("places.place_details_window");
  const apiT = useTranslations("api");
  const { routeToPlaceDetail } = usePlaceViewNavigation({
    onClose: handleReset,
  });

  const thirdChildRef = useRef(null);
  const containerRef = useRef(null);
  const [placeWindowLoader, setPlaceWindowLoader] = useState(true);
  const [isScrolledDown, setIsScrolledDown] = useState(false);
  const [placeMenus, setPlaceMenus] = useState([]);
  const [placePosts, setPlacePosts] = useState([]);
  const [placeTextReviews, setPlaceTextReviews] = useState([]);
  const [placesMenuLoader, setPlacesMenuLoader] = useState(true);

  const [showPlaceFavModal, setShowPlaceFavModal] = useState(false);

  const openPlaceFavModal = () => setShowPlaceFavModal(true);
  const closePlaceFavModal = () => setShowPlaceFavModal(false);

  const [placeDetails, setPlaceDetails] = useState(null);

  const [placeMedia, setPlaceMedia] = useState([]);

  const [currentTab, setCurrentTab] = useState("Home");

  const [refreshKey, setRefreshKey] = useState(Math.random());

  const scrollToThirdChild = () => {
    if (thirdChildRef?.current) {
      thirdChildRef.current.scrollIntoView({
        behavior: "smooth",
        top: 0,
      });
    }
  };

  const changeTabAndScroll = (tab) => {
    if (tab) {
      scrollToThirdChild();
      setCurrentTab(tab);
    }
  };

  const fetchPlaceDetailsById = async () => {
    if (!placeId) return;
    try {
      let response = await getAllPlacesWithReviewsByPlaceId(
        placeId,
        user?.userId
      );
      if (!(response?.id || response?._id)) {
        response = await getAllPlaceDetailsByPlaceId(placeId, user?.userId);
      }
      if (response?.id || response?._id || response?.contentId || response?.contentid) {
        const canonicalPlace = await resolveRicherCanonicalPlace(
          response,
          user?.userId
        );
        setPlaceDetails(canonicalPlace);
        routeToPlaceDetail(canonicalPlace);
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    } finally {
      setPlaceWindowLoader(false);
    }
  };

  const fetchPlaceMenuDetails = async () => {
    if (!placeId) return;
    try {
      const response = await getAllPlaceMenusByPlaceId(placeId);
      if (Array.isArray(response)) setPlaceMenus(response);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    } finally {
      setPlacesMenuLoader(false);
    }
  };

  const fetchPlaceTextReview = async () => {
    if (!placeId) return;
    try {
      const response = await getPlaceTextReviewByPlaceId(placeId);
      if (Array.isArray(response) && response?.length > 0) {
        setPlaceTextReviews(response);
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    }
  };

  const fetchPlacePosts = async () => {
    if (!placeId) return;
    try {
      const response = await getPlacePostsByPlaceId(placeId, {
        userId: user?.userId,
      });
      if (Array.isArray(response) && response?.length > 0) {
        setPlacePosts(response);
        if (placePosts?.length !== response?.length) {
          setRefreshKey(Math.random());
        }
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    }
  };

  const fetchAllPlaceData = () => {
    setPlaceWindowLoader(true);
    fetchPlaceDetailsById();
    fetchPlaceMenuDetails();
    fetchPlaceTextReview();
    fetchPlacePosts();
  };

  useEffect(() => {
    fetchAllPlaceData();
  }, [placeId, user]);

  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current.scrollTop > 5) setIsScrolledDown(true);
      else setIsScrolledDown(false);
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    // Cleanup event listener on component unmount
    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [placeId]);

  return (
    <div
      ref={containerRef}
      className="fixed w-screen h-screen top-0 left-0 right-0 bottom-0 bg-white text-black z-[99] overflow-x-hidden overflow-y-auto thin-scrollbar dark:bg-dark_bg_grey flex flex-col pb-20"
    >
      {placeWindowLoader ? (
        <div className="w-full h-full flex flex-col items-center translate-y-1/3 gap-5">
          <Iconify
            icon={"eos-icons:bubble-loading"}
            className={"!min-w-10 !h-10"}
          />
          <span className="text-xs text-black/60 dark:text-white/60">
            {t("finding more about this place")}
          </span>
        </div>
      ) : (
        <div className="w-full flex flex-col gap-2">
          <div
            className={`w-full flex items-center justify-between p-3 sticky top-0 left-0 z-50 ${
              isScrolledDown
                ? "bg-white dark:bg-dark_bg_grey border-b border-black/10 dark:border-brown"
                : "bg-transparent"
            } z-20`}
          >
            <div className="w-fit flex items-center gap-1">
              <button type="button" onClick={handleReset}>
                <Iconify icon={"uiw:left"} className={"!min-w-4 !h-4"} />
              </button>
              {isScrolledDown && (
                <p className="max-w-[330px] truncate dark:text-white">
                  {placeDetails?.name}
                </p>
              )}
            </div>
            <button type="button" onClick={handleReset}>
              <Iconify icon={"material-symbols-light:close"} />
            </button>
          </div>
          <PDW_PlaceMediaPhotoGrid
            placeId={placeId}
            className={"min-h-[300px] max-h-[300px] gap-0.5 -mt-14"}
            onClick={(tab) => changeTabAndScroll(tab)}
            setPlaceMedia={setPlaceMedia}
            changeTabAndScroll={changeTabAndScroll}
          />
          <PDW_PlaceDescription
            place={placeDetails}
            className={"px-4"}
            isCssForDialog={true}
            handleMapRoutesArrive={handleMapRoutesArrive}
            handleMapRoutesDepart={handleMapRoutesDepart}
            openPlaceFavModal={openPlaceFavModal}
            userLocation={userLocation}
          />
          {!showPlaceFavModal && (
            <PDW_PlaceTabsSection
              key={refreshKey}
              ref={thirdChildRef}
              className={"px-4"}
              scrollToThirdChild={scrollToThirdChild}
              place={placeDetails}
              placesMenuLoader={placesMenuLoader}
              placeMenus={placeMenus}
              placePosts={placePosts}
              placeTextReviews={placeTextReviews}
              userFollowing={userFollowing}
              setUserFollowing={setUserFollowing}
              isCssForDialog={true}
              placeMedia={placeMedia}
              currentTab={currentTab}
              setCurrentTab={setCurrentTab}
              handleContentModalOpen={handlePostCardClick}
              resetModal={fetchAllPlaceData}
              fetchPlacePosts={fetchPlacePosts}
              fetchPlaceTextReview={fetchPlaceTextReview}
            />
          )}
          <PlaceFavSliderModal
            place={placeDetails}
            placeMedia={placeMedia}
            isOpen={showPlaceFavModal}
            onClose={closePlaceFavModal}
            resetPlaceDetails={fetchAllPlaceData}
          />
        </div>
      )}
    </div>
  );
};

export default PlaceDetailsModalForMobile;
